import colors from '@/theme/variables/colors';

import { IAddress, IAddressForm } from './address';
import { IAuditModel, ITimestampsModel, ObjectId } from './base';
import { IPoint, IPolygonLayer } from './geo';
import { IIncidentCode } from './incident.code';
import {
  IMasterName,
  IMasterNamePerson,
  IMasterNameRelationBehavior,
} from './master.names';
import { ITaskType } from './tasks';
import { AssignedUnitShiftStatus, IUnitType } from './unit';
import { IUnitShift } from './unit.shift';
import { IUser, IUserRole } from './user';
import { IVendor } from './vendors';
import {
  IWantedPersonAttempt,
  IWantedPersonInvolvement,
} from './wanted.person';

import {
  MasterNameInvolvement,
  IMasterVehicle,
  IFile,
  IAgency,
  ICall,
} from '.';

export enum AssignmentStatus {
  NEW = 'NEW',
  PARTIAL_ASSIGNED = 'PARTIAL_ASSIGNED',
  ASSIGNED = 'ASSIGNED',
}

export interface IAddressAdditionalInfo {
  beat?: IPolygonLayer;
  municipality?: IPolygonLayer;
  address: string;
}

export interface ILocationPoint {
  _id: string;
  name?: string | null;
  point: IPoint;
  isPrimary?: boolean;
}

export interface IAddressInfo {
  address?: IAddress;
  businessName?: string | null;
  instructions?: string;
  additionalInfo?: IAddressAdditionalInfo;
  locationPoints?: Partial<ILocationPoint>[];
}

export enum TSeverity {
  'HIGH' = 'HIGH',
  'MEDIUM' = 'MEDIUM',
  'LOW' = 'LOW',
}

export enum ReceivedBy {
  OFFICER = 'OFFICER',
  RADIO_OTHER_AGENCY = 'RADIO_OTHER_AGENCY',
  TELEPHONE = 'TELEPHONE',
  MAIL = 'MAIL',
  OFFICIAL_CHANNELS = 'OFFICIAL_CHANNELS',
  DISTRICT_ATTORNEY = 'DISTRICT_ATTORNEY',
  WALK_IN = 'WALK_IN',
  TIPS_LINE = 'TIPS_LINE',
}

export const ReceivedByLabels: Record<ReceivedBy, string> = {
  [ReceivedBy.OFFICER]: 'Officer',
  [ReceivedBy.RADIO_OTHER_AGENCY]: 'Radio other agency',
  [ReceivedBy.TELEPHONE]: 'Telephone',
  [ReceivedBy.MAIL]: 'Mail',
  [ReceivedBy.OFFICIAL_CHANNELS]: 'Official channels',
  [ReceivedBy.DISTRICT_ATTORNEY]: 'District attorney',
  [ReceivedBy.WALK_IN]: 'Walk in',
  [ReceivedBy.TIPS_LINE]: 'Tips line',
};

export interface IAdditionalInfo {
  createdBy: {
    roles?: IUserRole[];
    _id?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
  };
  isTruncateToggleable?: boolean;
  description: string;
  isNew?: boolean;
  createdAt: string;
  isDeleted?: boolean;
  isEdited?: boolean;
  deletedAt?: string;
  _id?: string;
}

export interface IReporter {
  reportTime?: string;
  callerPhoneNumber?: string;
  contactPhoneNumber?: string;
  isCallingForSelf?: boolean;
  isKnowSubject?: boolean;
  notOnLocation?: boolean;
  notWantToBeSeen?: boolean;
  receivedBy?: ReceivedBy;
}

export interface IRequiredUnit {
  unitType: IUnitType;
  number: number;
}

export interface IRequiredUnitForm {
  unitType: string;
  number: number;
}

export interface IAssignedUnitShift extends ObjectId, ITimestampsModel {
  isDispatchedToHQ?: boolean;
  headquarterName?: string | null;
  isAssignedFromWaiting?: boolean;
  unitShift: IUnitShift;
  status: AssignedUnitShiftStatus;
  unAssignedAt?: Date;
}

export type ICFSInvolvement = {
  behavior?: IMasterNameRelationBehavior;
  involvement?: MasterNameInvolvement[];
};

export interface IAddressInfoForm {
  address?: IAddressForm;
  instructions?: string;
}

export interface ICFSMasterNameForm {
  cfsId: string;
  nameId: string;
  relationship: {
    behavior?: string;
    involvement: MasterNameInvolvement[];
  };
}

export interface ICFSForm extends ObjectId {
  agency?: Omit<IAgency, 'agency'>;
  assignedAgencies?: string[];
  number?: string;
  addressInfo?: IAddressInfoForm;
  severity: TSeverity;
  shortDescription?: string;
  incidentCodes: string[];
  requiredUnits?: IRequiredUnit[];
  reporter: IReporter;
  additionalInfo?: IAdditionalInfo[];
  receivedAt?: string;
  incomingCallId?: string;
  disposition?: string;
  incomingCallNotes?: string;
}

export interface ICFS
  extends Omit<
      ICFSForm,
      'incidentCodes' | 'addressInfo' | 'newAdditionalInfo' | 'requiredUnits'
    >,
    IAuditModel {
  number: string;
  addressInfo?: IAddressInfo;
  firstUnitArrivedAt?: string;
  isClosed: boolean;
  closeReason?: string;
  assignedAgencies?: string[];
  incidentCodes: IIncidentCode[];
  additionalInfo: IAdditionalInfo[];
  assignedUnitShifts: IAssignedUnitShift[];
  assignmentStatus: AssignmentStatus;
  isReadyToDispatch: boolean;
  requiredUnits: IRequiredUnit[];
  primaryReporter?: IMasterName;
  createdBy: IUser;
  names: number;
  linkedNames?: string[];
  vehicles: number;
  properties: number;
  narrativesCount?: number;
  relatedRecordsCount?: number;
  hasUnsetInvolvementName: boolean;
  linkedCases?: { _id: string; number: string }[];
  unitShiftDistances: IUnitShiftDistance[];
  wasReadyToDispatchAt?: Date | null;
  fireControlled?: Date | null;
  emailSentAt?: Date | null;
  primaryOfficer?: IUser | string;
  files?: IFile[];
  calls?: ICall[];
}

export interface IRecord extends IAuditModel {
  names: string;
  location: string;
  createdBy: string;
  status: string;
  dateApproved: Date;
  kaseId: number;
}

export interface ICFSCloseReason extends ObjectId {
  reason: string;
}

export interface ICFSMasterVehicle extends IAuditModel {
  cfs: string;
  masterVehicle: IMasterVehicle;
  relationship?: string;
}

export interface ICFSMasterName extends IAuditModel {
  cfs: string;
  masterName: IMasterNamePerson;
  relationship?: ICFSInvolvement;
  registeredOwnerVehicles?: string[];
}

export interface ICFSRelatedRecord extends IAuditModel {
  recordId: string;
  recordNumber: string;
  recordType: 'CASE' | 'CFS' | 'TASK' | 'WARRANT';
  agency?: string;
  isRestricted?: boolean;
  address?: string;
  title?: string;
  openedUrl?: string;
  status?: string;
  number?: string;
  shortDescription?: string;
  receivedAt?: string | Date;
  taskID?: string;
  taskType?: ITaskType | { type?: string };
  assignees?: Array<{
    fullName?: string;
    firstName?: string;
    lastName?: string;
  }>;
  processType?: string;
  disposition?: string;
  dateIssued?: string | Date;
  issuingCourt?: Pick<IVendor, 'name'> | string;
  assignedOfficer?:
    | {
        fullName?: string;
        firstName?: string;
        lastName?: string;
      }
    | string;
  attempts?: IWantedPersonAttempt[];
  persons?: IWantedPersonInvolvement[];
}

export interface ICFSRelatedRecordsResponse {
  count: number;
  records: ICFSRelatedRecord[];
}

export interface ICFSRelatedRecordMutation {
  recordId: string;
  recordType: ICFSRelatedRecord['recordType'];
}

export interface ICFSRelatedRecordSearchItem {
  _id: string;
  recordId: string;
  recordNumber: string;
  recordType: ICFSRelatedRecord['recordType'];
  address?: string;
  title?: string;
  status?: string;
  createdAt?: string | Date;
  openedUrl?: string;
  taskID?: string;
  taskType?: ITaskType | { type?: string };
  assignees?: Array<{
    fullName?: string;
    firstName?: string;
    lastName?: string;
  }>;
  processType?: string;
  disposition?: string;
  dateIssued?: string | Date;
  issuingCourt?: Pick<IVendor, 'name'> | string;
  assignedOfficer?:
    | {
        fullName?: string;
        firstName?: string;
        lastName?: string;
      }
    | string;
  attempts?: IWantedPersonAttempt[];
  persons?: IWantedPersonInvolvement[];
}

export interface ICFSRelatedRecordSearchResponse {
  records: ICFSRelatedRecordSearchItem[];
}

export enum CFSEvent {
  AGENCY_ASSIGNED = 'AGENCY_ASSIGNED',
  CALL_ANSWERED = 'CALL_ANSWERED',
  CALL_RECEIVED = 'CALL_RECEIVED',
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  UPDATED_ADDRESS_INFO = 'UPDATED_ADDRESS_INFO',
  UPDATED_INCIDENT_CODES = 'UPDATED_INCIDENT_CODES',
  UPDATED_REQUIRED_UNITS = 'UPDATED_REQUIRED_UNITS',
  UPDATED_ADDITIONAL_INFO = 'UPDATED_ADDITIONAL_INFO',
  UPDATED_AI_SHORT_DESCRIPTION = 'UPDATED_AI_SHORT_DESCRIPTION',
  UPDATED_FILES = 'UPDATED_FILES',
  CLOSED = 'CLOSED',
  REOPENED = 'REOPENED',
  READY_TO_DISPATCH = 'READY_TO_DISPATCH',
  UNREADY_TO_DISPATCH = 'UNREADY_TO_DISPATCH',
  UPDATED_PRIMARY_OFFICER = 'UPDATED_PRIMARY_OFFICER',

  // AssignedUnitShit events
  UNIT_SHIFT_ASSIGNED = 'UNIT_SHIFT_ASSIGNED',
  UNIT_SHIFT_IS_EN_ROUTE = 'UNIT_SHIFT_IS_EN_ROUTE',
  UNIT_SHIFT_IS_ON_SCENE = 'UNIT_SHIFT_IS_ON_SCENE',
  UNIT_SHIFT_IS_IN_PROGRESS = 'UNIT_SHIFT_IS_IN_PROGRESS',
  UNIT_SHIFT_COMPLETED = 'UNIT_SHIFT_COMPLETED',
  UNIT_SHIFT_CANCELED = 'UNIT_SHIFT_CANCELED',
  DISPATCH_TO_HQ_ENABLED = 'DISPATCH_TO_HQ_ENABLED',
  DISPATCH_TO_HQ_DISABLED = 'DISPATCH_TO_HQ_DISABLED',
  UNIT_SHIFT_IS_IN_THE_AREA = 'UNIT_SHIFT_IS_IN_THE_AREA',

  // CFS MasterName events
  LINKED_MASTER_NAME = 'LINKED_MASTER_NAME',
  UPDATED_MASTER_NAME_RELATIONSHIP = 'UPDATED_MASTER_NAME_RELATIONSHIP', // Updated relationship of cfs and master name
  UNLINKED_MASTER_NAME = 'UNLINKED_MASTER_NAME',

  // CFS MasterVehicle events
  LINKED_MASTER_VEHICLE = 'LINKED_MASTER_VEHICLE',
  UNLINKED_MASTER_VEHICLE = 'UNLINKED_MASTER_VEHICLE',
  ADDED_NARRATIVE = 'ADDED_NARRATIVE',
  UPDATED_NARRATIVE = 'UPDATED_NARRATIVE',
  DELETED_NARRATIVE = 'DELETED_NARRATIVE',
  MARK_FIRE_UNDER_CONTROL = 'MARK_FIRE_UNDER_CONTROL',
  GENERATED_AI_DESCRIPTION = 'GENERATED_AI_DESCRIPTION',
  UNIT_SHIFT_QUEUED = 'UNIT_SHIFT_QUEUED',

  // CFS Property events
  ADDED_PROPERTY = 'ADDED_PROPERTY',
  UPDATED_PROPERTY = 'UPDATED_PROPERTY',
  REMOVED_PROPERTY = 'REMOVED_PROPERTY',
  LOGGED_PROPERTY = 'LOGGED_PROPERTY',
  RELEASED_PROPERTY_TO_LAB = 'RELEASED_PROPERTY_TO_LAB',
  RELEASED_PROPERTY_TO_COURT = 'RELEASED_PROPERTY_TO_COURT',
  RELEASED_PROPERTY_TO_PERSON = 'RELEASED_PROPERTY_TO_PERSON',
  RELEASED_PROPERTY_TO_OWNER = 'RELEASED_PROPERTY_TO_OWNER',
  SEIZED_PROPERTY_IN_POLICE_CUSTODY = 'SEIZED_PROPERTY_IN_POLICE_CUSTODY',
  DISPOSED_PROPERTY = 'DISPOSED_PROPERTY',
  DESTROYED_PROPERTY = 'DESTROYED_PROPERTY',
  AUCTIONED_PROPERTY = 'AUCTIONED_PROPERTY',
  TRANSFERRED_PROPERTY = 'TRANSFERRED_PROPERTY',
  UPDATED_PROPERTY_LOCATION = 'UPDATED_PROPERTY_LOCATION',

  // Additional info events
  ADDED_ADDITIONAL_INFO = 'ADDED_ADDITIONAL_INFO',
  ADDITIONAL_INFO_DELETED = 'ADDITIONAL_INFO_DELETED',

  // Status / dispatch events
  UPDATED_CLOSE_REASON = 'UPDATED_CLOSE_REASON',
  REQUESTED_UNIT_SHIFT_DISTANCES = 'REQUESTED_UNIT_SHIFT_DISTANCES',
  RESEND_READY_TO_DISPATCH = 'RESEND_READY_TO_DISPATCH',
  PARTIAL_ASSIGNED = 'PARTIAL_ASSIGNED',
  ASSIGNED = 'ASSIGNED',

  // CFS MasterName (organization) events
  LINKED_ORGANIZATION = 'LINKED_ORGANIZATION',

  // CFS Files events
  EMAILED = 'EMAILED',

  // Caller Communication events
  CALLER_ADDED = 'CALLER_ADDED',
  CALLER_SMS_SENT = 'CALLER_SMS_SENT',
  CALLER_SMS_RESENT = 'CALLER_SMS_RESENT',
  CALLER_LINK_ACCEPTED = 'CALLER_LINK_ACCEPTED',
  CALLER_LOCATION_SHARED = 'CALLER_LOCATION_SHARED',
  CALLER_FILE_UPLOADED = 'CALLER_FILE_UPLOADED',
  CALLER_MESSAGE_SENT = 'CALLER_MESSAGE_SENT',

  CUSTOM_EVENT = 'CUSTOM_EVENT',
}

export const CFS_PROPERTY_CUSTODY_EVENTS: CFSEvent[] = [
  CFSEvent.SEIZED_PROPERTY_IN_POLICE_CUSTODY,
  CFSEvent.RELEASED_PROPERTY_TO_LAB,
  CFSEvent.RELEASED_PROPERTY_TO_COURT,
  CFSEvent.RELEASED_PROPERTY_TO_PERSON,
  CFSEvent.UPDATED_PROPERTY_LOCATION,
  CFSEvent.RELEASED_PROPERTY_TO_OWNER,
  CFSEvent.DISPOSED_PROPERTY,
  CFSEvent.DESTROYED_PROPERTY,
  CFSEvent.AUCTIONED_PROPERTY,
  CFSEvent.TRANSFERRED_PROPERTY,
];

export const PINNED_LIVE_INFO_EVENT = 'PINNED_LIVE_INFO';

export const CFS_TIMELINE_EVENTS = [
  CFSEvent.CALL_RECEIVED,
  CFSEvent.CALL_ANSWERED,
  CFSEvent.CREATED,
  CFSEvent.UPDATED_INCIDENT_CODES,
  CFSEvent.UNIT_SHIFT_ASSIGNED,
  CFSEvent.UNIT_SHIFT_IS_EN_ROUTE,
  CFSEvent.UNIT_SHIFT_IS_ON_SCENE,
  CFSEvent.UNIT_SHIFT_COMPLETED,
  CFSEvent.UNIT_SHIFT_CANCELED,
  CFSEvent.MARK_FIRE_UNDER_CONTROL,
  CFSEvent.CLOSED,
  CFSEvent.UNIT_SHIFT_IS_IN_THE_AREA,
  CFSEvent.CUSTOM_EVENT,
  PINNED_LIVE_INFO_EVENT,
];

export const CFSEvent_STATUS = {
  [CFSEvent.CREATED]: {
    label: 'Created',
    color: 'white',
    bgColor: colors.secondary.main,
  },
  [CFSEvent.UPDATED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UPDATED_ADDRESS_INFO]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UPDATED_INCIDENT_CODES]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UPDATED_REQUIRED_UNITS]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UPDATED_ADDITIONAL_INFO]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },

  [CFSEvent.UPDATED_AI_SHORT_DESCRIPTION]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UPDATED_MASTER_NAME_RELATIONSHIP]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.ADDED_NARRATIVE]: {
    label: 'Added',
    color: 'white',
    bgColor: colors.secondary.main,
  },
  [CFSEvent.UPDATED_NARRATIVE]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.DELETED_NARRATIVE]: {
    label: 'Deleted',
    color: 'white',
    bgColor: colors.error.main,
  },
  [CFSEvent.CLOSED]: {
    label: 'Closed',
    color: 'black',
    bgColor: colors.grey[10],
  },
  [CFSEvent.REOPENED]: {
    label: 'Reopened',
    color: 'white',
    bgColor: colors.warning.main,
  },
  [CFSEvent.LINKED_MASTER_NAME]: {
    label: 'Linked',
    color: 'black',
    bgColor: colors.grey[10],
  },
  [CFSEvent.UNLINKED_MASTER_NAME]: {
    label: 'Unlinked',
    color: 'black',
    bgColor: colors.grey[10],
  },

  [CFSEvent.AGENCY_ASSIGNED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_ASSIGNED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_IS_EN_ROUTE]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_IS_IN_THE_AREA]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_IS_ON_SCENE]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_IS_IN_PROGRESS]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_COMPLETED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_CANCELED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.DISPATCH_TO_HQ_ENABLED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.DISPATCH_TO_HQ_DISABLED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },

  [CFSEvent.READY_TO_DISPATCH]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.LINKED_MASTER_VEHICLE]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNLINKED_MASTER_VEHICLE]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.GENERATED_AI_DESCRIPTION]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.MARK_FIRE_UNDER_CONTROL]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UPDATED_FILES]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UPDATED_PRIMARY_OFFICER]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.UNIT_SHIFT_QUEUED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.ADDED_PROPERTY]: {
    label: 'Added',
    color: 'white',
    bgColor: colors.secondary.main,
  },
  [CFSEvent.UPDATED_PROPERTY]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.REMOVED_PROPERTY]: {
    label: 'Deleted',
    color: 'white',
    bgColor: colors.error.main,
  },
  [CFSEvent.LOGGED_PROPERTY]: {
    label: 'Logged',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.RELEASED_PROPERTY_TO_LAB]: {
    label: 'Released',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.RELEASED_PROPERTY_TO_COURT]: {
    label: 'Released',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.RELEASED_PROPERTY_TO_PERSON]: {
    label: 'Released',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.RELEASED_PROPERTY_TO_OWNER]: {
    label: 'Released',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.SEIZED_PROPERTY_IN_POLICE_CUSTODY]: {
    label: 'Seized',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.DISPOSED_PROPERTY]: {
    label: 'Disposed',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.DESTROYED_PROPERTY]: {
    label: 'Destroyed',
    color: 'white',
    bgColor: colors.error.main,
  },
  [CFSEvent.AUCTIONED_PROPERTY]: {
    label: 'Auctioned',
    color: 'white',
    bgColor: colors.error.main,
  },
  [CFSEvent.TRANSFERRED_PROPERTY]: {
    label: 'Transfered',
    color: 'white',
    bgColor: colors.error.main,
  },
  [CFSEvent.UPDATED_PROPERTY_LOCATION]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.error.main,
  },
  [CFSEvent.ADDED_ADDITIONAL_INFO]: {
    label: 'Added',
    color: 'white',
    bgColor: colors.secondary.main,
  },
  [CFSEvent.ADDITIONAL_INFO_DELETED]: {
    label: 'Deleted',
    color: 'white',
    bgColor: colors.error.main,
  },
  [CFSEvent.UPDATED_CLOSE_REASON]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.REQUESTED_UNIT_SHIFT_DISTANCES]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.RESEND_READY_TO_DISPATCH]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.PARTIAL_ASSIGNED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.ASSIGNED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.LINKED_ORGANIZATION]: {
    label: 'Linked',
    color: 'black',
    bgColor: colors.grey[10],
  },
  [CFSEvent.EMAILED]: {
    label: 'Emailed',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.CALLER_ADDED]: {
    label: 'Added',
    color: 'white',
    bgColor: colors.secondary.main,
  },
  [CFSEvent.CALLER_SMS_SENT]: {
    label: 'Sent',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.CALLER_SMS_RESENT]: {
    label: 'Resent',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.CALLER_LINK_ACCEPTED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.CALLER_LOCATION_SHARED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.CALLER_FILE_UPLOADED]: {
    label: 'Updated',
    color: 'white',
    bgColor: colors.primary.main,
  },
  [CFSEvent.CALLER_MESSAGE_SENT]: {
    label: 'Sent',
    color: 'white',
    bgColor: colors.primary.main,
  },
};

export interface ICFSLog extends IAuditModel {
  app: string;
  cfs: string;
  database: string;
  event: CFSEvent;
  field: string;
  data?: {
    new?: Record<string, any>;
    deleted?: any;
  };
  logCollection: string;
  collection: string;
  createdBy: IUser;
  editedBy?: IUser;
  editedAt?: number;
  overriddenAt?: number | null;
  groupDay?: number | null;
  sortOrder?: number;
  account: string;
}

export interface IRequiredUnitsLog {
  unitType?: string;
  number?: string;
}

export interface IAssignedUnitShiftsLog {
  unitShift?: string;
  status?: string;
}

export interface IUnitShiftDistance
  extends Omit<ITimestampsModel, 'updatedAt'> {
  unitShift: string;
  distance: number;
}

export interface ICallerLocation {
  altitude: string;
  application: string;
  call_start_time: string;
  caller_id: string;
  caller_name?: string;
  created_time: string;
  format_version: string;
  latitude: string;
  location_time: string;
  longitude: string;
  /**
   * Technology used to obtain location e.g. 'gps', 'triangulation', 'cell', 'wifi'
   */
  positioning_method?: string;
  /**
   * URL associated with reverse-geocoded place for obtaining additional data
   */
  rg_additional_info_url?: string;
  rg_address?: string;
  /**
   * Contact info e.g. email or telephone number of the reverse-geocoded place
   */
  rg_contact?: string;
  /**
   * Source of the reverse-geocoded address (Possible values: 'google' or 'apple')
   */
  rg_engine?: 'google' | 'apple';
  /**
   * If known, the floor and/or room of the reverse-geocoded address
   */
  rg_exact_location?: string;
  rg_id?: string;
  rg_latitude?: string;
  /**
   * Integer value from 0-100 of the % likelihood that the location record is at this place
   */
  rg_likelihood?: string;
  rg_longitude?: string;
  rg_name?: string;
  source: string;
  /**
   * IETF RFC7459 defined integer value from 0-100 of the % confidence in the uncertainty radius
   */
  uncertainty_confidence?: string;
  /**
   * IETF RFC7459 defined probability distribution function (PDF) used to determine uncertainty_confidence
   * (Possible values: 'normal', 'rectangular' or 'unknown')
   */
  uncertainty_confidence_pdf?: 'normal' | 'rectangular' | 'unknown';
  uncertainty_radius: string;
}

export enum SMSStatus {
  NOT_SENT = 'NOT_SENT',
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  EXPIRED = 'EXPIRED',
}

export interface ICFSCaller extends IAuditModel {
  phoneNumber: string;
  callerId: string; // Unique ID like YYMM0001, YYMM0002, etc.
  smsStatus: SMSStatus;
  sentAt?: string;
  expiresAt?: string;
  secureLink?: string;
  fullName?: string;
}

export enum CFSEventMapper {
  CALL_RECEIVED = 'Call Received',
  CALL_ANSWERED = 'Call Answered by Dispatcher',
  CREATED = 'Created',
  UPDATED_INCIDENT_CODES = 'Updated Incident Codes',
  UNIT_SHIFT_ASSIGNED = 'Assigned',
  UNIT_SHIFT_IS_EN_ROUTE = 'En Route',
  UNIT_SHIFT_IS_ON_SCENE = 'On Scene',
  UNIT_SHIFT_IS_IN_PROGRESS = 'In Progress',
  UNIT_SHIFT_CANCELED = 'Canceled',
  UNIT_SHIFT_COMPLETED = 'Completed',
  MARK_FIRE_UNDER_CONTROL = 'Fire Under Control',
  CLOSED = 'Closed',
  REOPENED = 'Reopened',
  SEVERITY_CHANGED = 'Severity Changed',
  STATUS_CHANGED = 'Status Changed',
  PINNED_LIVE_INFO = 'Pinned Live Info',
  CUSTOM_EVENT = 'Custom Event',
}
